import Chart from "chart.js/auto";
import themeConfig from "../../../theme.json";

const FALLBACK_COLOR = "#7c8ba3";
const themePalette = themeConfig?.settings?.color?.palette || [];
const themePaletteMap = themePalette.reduce((acc, entry) => {
  if (entry?.slug) {
    acc[entry.slug] = entry.color;
  }
  return acc;
}, {});
const DEFAULT_COLOR_TOKEN =
  (themePaletteMap.main ? "main" : undefined) ||
  themePalette[0]?.slug ||
  FALLBACK_COLOR;
const hasDomAccess = typeof document !== "undefined";
const colorParserCanvas = hasDomAccess
  ? document.createElement("canvas")
  : null;
const colorParserCtx = colorParserCanvas
  ? colorParserCanvas.getContext("2d")
  : null;

const resolveCssVariableColor = (value) => {
  if (
    !value ||
    typeof value !== "string" ||
    !value.includes("var(") ||
    !hasDomAccess
  ) {
    return value;
  }

  const match = value.match(/var\((--[^,\s)]+)(?:,\s*([^)]+))?\)/);
  if (!match) return value;

  const [, variableName, fallback] = match;
  const computed = getComputedStyle(document.documentElement).getPropertyValue(
    variableName
  );
  const resolved = computed?.trim();
  if (resolved) return resolved;
  if (fallback) {
    return resolveCssVariableColor(fallback.trim());
  }
  return value;
};

const resolveThemeColor = (token) => {
  if (!token) return "";
  const paletteColor = themePaletteMap[token];
  const rawColor = paletteColor || token;
  const resolved = resolveCssVariableColor(rawColor);
  if (resolved && !resolved.includes("var(")) {
    return resolved;
  }
  if (rawColor && !rawColor.includes("var(")) {
    return rawColor;
  }
  return FALLBACK_COLOR;
};

const ensureGraphColorToken = (graphEl) => {
  const providedToken = graphEl.dataset.color?.trim();
  if (providedToken) {
    return providedToken;
  }
  if (DEFAULT_COLOR_TOKEN) {
    graphEl.dataset.color = DEFAULT_COLOR_TOKEN;
    return DEFAULT_COLOR_TOKEN;
  }
  return FALLBACK_COLOR;
};

const getGraphBaseColor = (graphEl) => {
  const token = ensureGraphColorToken(graphEl);
  const resolved = resolveThemeColor(token);
  return resolved || FALLBACK_COLOR;
};

const parseColorToRgb = (color) => {
  if (!colorParserCtx || !color) return null;
  try {
    colorParserCtx.fillStyle = "#000";
    colorParserCtx.fillStyle = color;
    const normalized = colorParserCtx.fillStyle;
    if (!normalized) return null;

    if (normalized.startsWith("#")) {
      let hex = normalized.slice(1);
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }
      if (hex.length === 8) {
        hex = hex.slice(0, 6);
      }
      if (hex.length !== 6) return null;
      const int = Number.parseInt(hex, 16);
      if (Number.isNaN(int)) return null;
      return {
        r: (int >> 16) & 255,
        g: (int >> 8) & 255,
        b: int & 255,
      };
    }

    const rgbaMatch = normalized
      .replace(/\s+/g, "")
      .match(/^rgba?\((\d+),(\d+),(\d+)/i);
    if (rgbaMatch) {
      return {
        r: Number(rgbaMatch[1]),
        g: Number(rgbaMatch[2]),
        b: Number(rgbaMatch[3]),
      };
    }
  } catch (error) {
    // ignore parse errors and fall back later
  }
  return null;
};

const generateDatasetColors = (baseColor, count) => {
  if (!count) return [];
  const rgb = parseColorToRgb(baseColor);
  if (!rgb) {
    return Array(count).fill(baseColor || FALLBACK_COLOR);
  }
  return Array.from({ length: count }, (_, index) => {
    const alpha = Math.max(0, (count - index) / count);
    const roundedAlpha = Math.round(alpha * 100) / 100;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${roundedAlpha})`;
  });
};

const parseGraphDataset = (rawData) => {
  if (!rawData) return [];
  const normalized = rawData.trim();
  if (!normalized) return [];

  const toNumbers = (values) =>
    values.map(Number).filter((value) => Number.isFinite(value));

  try {
    const parsed = JSON.parse(normalized);
    if (Array.isArray(parsed)) {
      return toNumbers(parsed);
    }
  } catch (error) {
    // ignore and fall back to manual parsing
  }

  return toNumbers(
    normalized
      .replace(/[\[\]]/g, "")
      .split(/[\s,]+/)
      .filter(Boolean)
  );
};

const toFiniteNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (value == null) return null;
  const numericText = `${value}`.replace(/[^0-9.+-]/g, "");
  if (!numericText) return null;
  const parsed = Number(numericText);
  return Number.isFinite(parsed) ? parsed : null;
};

const getGraphContextRoot = (graphEl) =>
  graphEl.closest(".js-databox-graph") ||
  graphEl.closest(".js-databox-num") ||
  graphEl.closest(".js-databox") ||
  graphEl.parentElement;

const collectDatasetFromDom = (graphEl) => {
  const context = getGraphContextRoot(graphEl);
  if (!context) return [];
  return Array.from(context.querySelectorAll(".js-graph-num"))
    .map((node) => toFiniteNumber(node.textContent))
    .filter((value) => Number.isFinite(value));
};

const getGraphDataset = (graphEl) => {
  const datasetAttr = graphEl.dataset.graph;
  const parsedDataset = parseGraphDataset(datasetAttr);
  if (parsedDataset.length) {
    return parsedDataset;
  }
  const domDataset = collectDatasetFromDom(graphEl);
  if (domDataset.length) {
    graphEl.dataset.graph = JSON.stringify(domDataset);
  }
  return domDataset;
};

const getGraphLabels = (graphEl, count, appendedRemainder) => {
  const graphWrapper = graphEl.closest(".js-databox-graph");
  if (graphWrapper) {
    const definitionTerms = graphWrapper.querySelectorAll("dt");
    if (definitionTerms.length) {
      return Array.from(definitionTerms)
        .slice(0, count)
        .map((node) => node.textContent.trim());
    }
  }

  const scopedLabel =
    graphEl
      .closest(".js-databox-num")
      ?.querySelector(".js-databox-num-label") ||
    graphEl.closest(".js-databox")?.querySelector(".js-databox-label");

  if (scopedLabel) {
    const labelText = scopedLabel.textContent.trim();
    return appendedRemainder ? [labelText, "その他"] : [labelText];
  }

  return appendedRemainder ? ["割合", "その他"] : [];
};

const renderPieChart = (graphEl) => {
  if (graphEl.dataset.graphReady === "true") return;

  const dataset = getGraphDataset(graphEl);
  if (!dataset.length) return;

  const shouldAppendRemainder = dataset.length === 1 && dataset[0] <= 100;
  const preparedData = shouldAppendRemainder
    ? [...dataset, Math.max(0, 100 - dataset[0])]
    : dataset;

  const labels = getGraphLabels(
    graphEl,
    preparedData.length,
    shouldAppendRemainder
  );
  const baseColor = getGraphBaseColor(graphEl);
  const backgroundColors = generateDatasetColors(
    baseColor,
    preparedData.length
  );

  const canvas = document.createElement("canvas");
  graphEl.replaceChildren(canvas);
  graphEl.dataset.graphReady = "true";

  new Chart(canvas, {
    type: "pie",
    data: {
      labels,
      datasets: [
        {
          data: preparedData,
          borderWidth: 0,
          backgroundColor: backgroundColors,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    },
  });
};

const initPieCharts = () => {
  if (!hasDomAccess) return;
  const graphElements = document.querySelectorAll(".js-graph");
  if (!graphElements.length) return;
  graphElements.forEach(renderPieChart);
};

export { initPieCharts as default };
